import { Check, ChevronDown, Filter } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { cn } from "~/lib/utils";
import { AgentListView } from "~/services/agents";

interface ViewMeta {
  /** Short label — on the collapsed trigger and each menu item's title. */
  label: string;
  /** One-line explanation shown muted beneath the label in the open menu. */
  description: string;
}

const VIEW_META: Record<AgentListView, ViewMeta> = {
  [AgentListView.USABLE]: {
    label: "Can use",
    description: "Agents you can use (including those you manage)",
  },
  [AgentListView.EDITABLE]: {
    label: "Can manage",
    description: "Agents where you're an Editor or Owner",
  },
  [AgentListView.ALL]: {
    label: "All",
    description: "Admin-only list of all agents",
  },
};

interface AgentViewFilterProps {
  value: AgentListView;
  /** The views the caller may pick, in menu order. The control hides itself when
   *  fewer than two are offered — a single choice isn't a choice. */
  options: AgentListView[];
  onChange: (view: AgentListView) => void;
}

/** Access-scope selector for the agents list: a compact trigger label that
 *  expands into per-option descriptions in the open menu. The caller decides
 *  which options appear; this component is presentational. */
export const AgentViewFilter = ({ value, options, onChange }: AgentViewFilterProps) => {
  if (options.length < 2) {
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="min-w-36 justify-between text-xs"
          aria-label={`View: ${VIEW_META[value].label}`}
        >
          <span className="flex items-center gap-1.5">
            <Filter className="size-4" aria-hidden="true" />
            {VIEW_META[value].label}
          </span>
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-64">
        {options.map((view) => {
          const meta = VIEW_META[view];
          const isActive = view === value;
          return (
            <DropdownMenuItem
              key={view}
              onSelect={() => onChange(view)}
              className="items-start gap-2 py-2"
            >
              <Check
                className={cn("mt-0.5 size-4 shrink-0", !isActive && "opacity-0")}
                aria-hidden="true"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium leading-none">{meta.label}</span>
                <span className="text-muted-foreground text-xs">{meta.description}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
